import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { LinearProgress, GridLegacy as Grid } from '@mui/material';
import PointsDataTable from '../elements/PointsDataTable';
import TimeRangeFilterPopover, {
  TimeRangeOptions,
} from '../elements/filter/TimeRangeFilterPopover';
import getBuildingStaging from '../../../redux/selectors/staging';
import SliderFilterPopover, { SliderOptions } from '../elements/filter/SliderFilterPopover';
import TextFilterPopover from '../elements/filter/TextFilterPopover';
import PointTypeFilterPopover from '../elements/filter/PointTypeFilterPopover';
import BooleanFilterOption from '../elements/filter/BooleanFilterOption';
import StagingFilterBuilder, { FilterDefinition } from '../elements/StagingFilterBuilder';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import EquipmentLoader from '../../loaders/EquipmentLoader';
import LocationLoader from '../../loaders/LocationLoader';
import {
  PointTypeFilter,
  ContainsTextFilter,
  RangeFilter,
  TimeRangeFilter,
  BooleanFilter,
} from '../../../redux/constants/filters';
import { FilterProvider } from '../contexts/FilterContext';
import { setPage } from '../../../redux/actions/staging';
import { Page, Row } from '../../util/Page';

const PointsView = ({ buildingId, loaded, points, resetPage }) => {
  const [selectedTopics, setSelectedTopics] = useState([]);

  useEffect(() => resetPage, [resetPage]);

  // reset selections on building change

  useEffect(() => setSelectedTopics([]), [buildingId, setSelectedTopics]);

  const onTopicSelected = (newTopics) => {
    setSelectedTopics(newTopics);
  };

  const hideOrphans = useMemo(
    () =>
      new FilterDefinition(
        BooleanFilterOption,
        'hide_orphans',
        'Hide points with no equip',
        {},
        () =>
          new BooleanFilter({
            key: 'hide_orphans',
            getter: (p) => p.equip,
            evaluation: (equip) => (equip?.length ?? 0) > 0,
          }),
        (val) => val
      ),
    []
  );
  const filterDefs = useMemo(
    () => [
      new FilterDefinition(
        BooleanFilterOption,
        'hide_valid',
        'Invalid Status',
        {},
        () =>
          new BooleanFilter({
            key: 'hide_valid',
            getter: (p) => p?.validation,
            evaluation: (validation) => validation === undefined || validation.length !== 0,
          }),
        (val) => val
      ),
      new FilterDefinition(
        PointTypeFilterPopover,
        'point_type',
        'Point Type',
        {},
        (type) =>
          new PointTypeFilter({
            key: 'point_type',
            getter: (p) => p.point_type_id,
            type,
          }),
        (type) => type.name
      ),
      new FilterDefinition(
        SliderFilterPopover,
        'point_confidence',
        'Point Type Confidence',
        new SliderOptions([0, 100], [50, 100]),
        (v) =>
          new RangeFilter({
            key: 'point_confidence',
            getter: (point) => Math.round(point.confidences.point_type_id),
            minimum: v[0],
            maximum: v[1],
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'pointName',
        'Point Name',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'pointName',
            getter: (p) => p.properties?.objectName,
            fragment: v,
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'pointDescription',
        'Description',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'pointDescription',
            getter: (p) => p.properties?.description,
            fragment: v,
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'equip_name',
        'Equip Name',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'equip_name',
            getter: (point) => (point.equip ?? []).map((equip) => equip.name).join(' '),
            fragment: v,
          })
      ),
      new FilterDefinition(
        TimeRangeFilterPopover,
        'first_discovered',
        'First Discovered Time',
        new TimeRangeOptions(
          points
            ?.values()
            .map((p) => Date.parse(p.created))
            ?.reduce((acc, val) => (acc === null ? val : Math.min(acc, val)), null) ?? 0,
          // eslint-disable-next-line react-hooks/purity
          Date.now()
        ),
        ({ from, to }) =>
          new TimeRangeFilter({
            key: 'first_discovered',
            getter: (point) => Date.parse(point.created),
            minimum: from,
            maximum: to,
          })
      ),
      hideOrphans,
    ],
    [hideOrphans, points]
  );

  if (!loaded) {
    return (
      <Page>
        <Row>
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <EquipmentLoader key={buildingId} buildingId={buildingId} />
          <LocationLoader key={`ll-${buildingId}`} buildingId={buildingId} />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  return (
    <FilterProvider initialValue={[hideOrphans.filterBuilder(true)]} onFilterChange={resetPage}>
      <Grid item xs={12}>
        <StagingFilterBuilder filterDefs={filterDefs} />
      </Grid>
      <Grid item xs={12}>
        <PointsDataTable
          buildingId={buildingId}
          selectedTopics={selectedTopics}
          onItemSelected={onTopicSelected}
          points={points}
        />
      </Grid>
    </FilterProvider>
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  ...getBuildingStaging(state, buildingId),
  loaded: state.entities.staging.loaded[buildingId] ?? false,
  buildingId,
});

const mapDispatchToProps = (dispatch) => ({
  resetPage: () => dispatch(setPage(0)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PointsView);
